<template>
  <div class="date-picker-container">
    <div class="date-picker">
      <button 
        @click="decrementDate" 
        class="date-button prev-button"
        :disabled="isMinDate"
        title="Previous day"
      >
        <svg class="button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
      </button>

      <div class="date-picker-center">
        <button 
          v-if="showQuickNav"
          @click="goToYesterday" 
          class="quick-button"
          :class="{ active: isYesterday }"
          :disabled="isYesterday"
          :title="isYesterday ? 'Already viewing yesterday' : 'Go to yesterday'"
        >
          Yesterday
        </button>

        <div class="date-display">
        <div class="date-input-wrapper" ref="wrapper">
          <div class="date-field" ref="dateField" :class="{ 'is-open': showPopup }">
            <input
              type="text"
              v-model="inputText"
              @input="onInputType"
              @keydown.enter.prevent="commitInput"
              @keydown.esc.prevent="closePopup"
              @blur="commitInput"
              @focus="openPopup"
              class="date-input"
              placeholder="DD.MM.YYYY"
              maxlength="10"
              inputmode="numeric"
              title="Type DD.MM.YYYY or click the calendar icon"
            />
            <button
              type="button"
              class="calendar-icon-btn"
              @mousedown.prevent
              @click.stop="togglePopup"
              title="Open calendar"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="5" width="18" height="16" rx="2"/>
                <path d="M3 9h18M8 3v4M16 3v4"/>
              </svg>
            </button>
          </div>

          <div class="date-info">
            <span class="date-formatted">{{ formattedDisplayDate }}</span>
            <span class="date-separator">|</span>
            <span class="day-name">{{ dayName }}</span>
          </div>
        </div>
        </div>

        <button 
          v-if="showQuickNav"
          @click="goToToday" 
          class="quick-button"
          :class="{ active: isToday }"
          :disabled="isToday"
          :title="isToday ? 'Already viewing today' : 'Go to today'"
        >
          Today
        </button>
      </div>

      <button 
        @click="incrementDate" 
        class="date-button next-button"
        :disabled="isMaxDate"
        title="Next day"
      >
        <svg class="button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="showPopup"
        ref="popup"
        class="calendar-popup"
        :style="popupStyle"
        @click.stop
        @mousedown.prevent
      >
        <div class="calendar-popup-header">
          <button type="button" class="cal-nav-btn" @click="prevMonth" :disabled="!canGoPrevMonth">‹</button>
          <span class="cal-month-label">{{ monthYearLabel }}</span>
          <button type="button" class="cal-nav-btn" @click="nextMonth" :disabled="!canGoNextMonth">›</button>
        </div>
        <div class="calendar-weekdays">
          <span v-for="d in weekdays" :key="d">{{ d }}</span>
        </div>
        <div class="calendar-grid">
          <button
            v-for="cell in calendarCells"
            :key="cell.key"
            type="button"
            class="cal-day"
            :class="{
              'is-other-month': !cell.isCurrentMonth,
              'is-today': cell.isToday,
              'is-selected': cell.isSelected,
            }"
            :disabled="cell.isDisabled"
            @mousedown.prevent
            @click="selectCell(cell)"
          >{{ cell.day }}</button>
        </div>
        <div class="calendar-popup-footer">
          <button type="button" class="cal-footer-btn" @click="selectToday">Today</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'DatePicker',
  props: {
    dateValue: {
      type: String, // Expecting a date in 'YYYY-MM-DD' format
      required: true,
    },
    minDate: {
      type: String,
      default: '2020-01-01'
    },
    maxDate: {
      type: String,
      default: '2030-12-31'
    },
    showQuickNav: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      inputText: '',
      showPopup: false,
      popupStyle: {},
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth(),
    };
  },
  watch: {
    dateValue: {
      immediate: true,
      handler() {
        this.inputText = this.formattedDottedDate;
      }
    }
  },
  computed: {
    formattedDottedDate() {
      const date = new Date(this.dateValue + 'T00:00:00');
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}.${month}.${year}`;
    },
    weekdays() {
      return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    },
    monthYearLabel() {
      return new Date(this.viewYear, this.viewMonth, 1)
        .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    canGoPrevMonth() {
      const firstOfView = new Date(this.viewYear, this.viewMonth, 1);
      const min = new Date(this.minDate + 'T00:00:00');
      return firstOfView > new Date(min.getFullYear(), min.getMonth(), 1);
    },
    canGoNextMonth() {
      const firstOfView = new Date(this.viewYear, this.viewMonth, 1);
      const max = new Date(this.maxDate + 'T00:00:00');
      return firstOfView < new Date(max.getFullYear(), max.getMonth(), 1);
    },
    calendarCells() {
      const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
      const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
      const gridStart = new Date(this.viewYear, this.viewMonth, 1 - mondayOffset);

      const today = new Date();
      const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const selected = new Date(this.dateValue + 'T00:00:00');
      const min = new Date(this.minDate + 'T00:00:00');
      const max = new Date(this.maxDate + 'T00:00:00');

      const cells = [];
      for (let i = 0; i < 42; i++) {
        const cellDate = new Date(gridStart);
        cellDate.setDate(gridStart.getDate() + i);
        const dateStr = this.toYmd(cellDate);
        const cellLocal = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());

        cells.push({
          key: dateStr,
          day: cellDate.getDate(),
          dateStr,
          isCurrentMonth: cellDate.getMonth() === this.viewMonth,
          isToday: cellLocal.getTime() === todayLocal.getTime(),
          isSelected: cellLocal.getTime() === selected.getTime(),
          isDisabled: cellLocal < min || cellLocal > max,
        });
      }
      return cells;
    },
    dayName() {
      const date = new Date(this.dateValue + 'T00:00:00');
      const fullDayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const shortDay = fullDayName.substring(0, 3);
      return shortDay.charAt(0).toUpperCase() + shortDay.slice(1).toLowerCase();
    },
    formattedDisplayDate() {
      const date = new Date(this.dateValue + 'T00:00:00');
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    },
    isToday() {
      const today = new Date();
      const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const selectedDate = new Date(this.dateValue + 'T00:00:00');
      return todayLocal.getTime() === selectedDate.getTime();
    },
    isMinDate() {
      return this.dateValue <= this.minDate;
    },
    isMaxDate() {
      return this.dateValue >= this.maxDate;
    },
    isYesterday() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayLocal = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
      const selectedDate = new Date(this.dateValue + 'T00:00:00');
      return yesterdayLocal.getTime() === selectedDate.getTime();
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    this.removePopupListeners();
  },
  methods: {
    toYmd(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    parseDottedDate(text) {
      const match = (text || '').match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (!match) return null;
      const [, dayStr, monthStr, yearStr] = match;
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return null;
      }
      return `${yearStr}-${monthStr}-${dayStr}`;
    },
    onInputType(event) {
      const digits = event.target.value.replace(/\D/g, '').slice(0, 8);
      let formatted = digits;
      if (digits.length > 4) {
        formatted = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
      } else if (digits.length > 2) {
        formatted = `${digits.slice(0, 2)}.${digits.slice(2)}`;
      }
      this.inputText = formatted;
    },
    commitInput() {
      const ymd = this.parseDottedDate(this.inputText);
      if (ymd && this.validateDate(ymd)) {
        this.updateDate(ymd);
        this.closePopup();
      } else {
        this.inputText = this.formattedDottedDate;
      }
    },
    togglePopup() {
      if (this.showPopup) {
        this.closePopup();
      } else {
        this.openPopup();
      }
    },
    openPopup() {
      this.showPopup = true;
      const date = new Date(this.dateValue + 'T00:00:00');
      this.viewYear = date.getFullYear();
      this.viewMonth = date.getMonth();
      this.$nextTick(() => {
        this.updatePopupPosition();
        this.addPopupListeners();
      });
    },
    closePopup() {
      this.showPopup = false;
      this.inputText = this.formattedDottedDate;
      this.popupStyle = {};
      this.removePopupListeners();
    },
    updatePopupPosition() {
      const anchor = this.$refs.dateField;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const isMobile = window.innerWidth <= 750;

      if (isMobile) {
        this.popupStyle = {
          position: 'fixed',
          top: `${rect.bottom + 8}px`,
          left: `${rect.left}px`,
          transform: 'none',
          width: `${Math.min(rect.width, 280)}px`,
          maxWidth: '280px',
          zIndex: '9999',
        };
      } else {
        this.popupStyle = {
          position: 'fixed',
          top: `${rect.bottom + 8}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translateX(-50%)',
          width: '280px',
          zIndex: '9999',
        };
      }
    },
    addPopupListeners() {
      window.addEventListener('resize', this.updatePopupPosition);
      window.addEventListener('scroll', this.updatePopupPosition, true);
    },
    removePopupListeners() {
      window.removeEventListener('resize', this.updatePopupPosition);
      window.removeEventListener('scroll', this.updatePopupPosition, true);
    },
    prevMonth() {
      if (!this.canGoPrevMonth) return;
      if (this.viewMonth === 0) {
        this.viewYear -= 1;
        this.viewMonth = 11;
      } else {
        this.viewMonth -= 1;
      }
    },
    nextMonth() {
      if (!this.canGoNextMonth) return;
      if (this.viewMonth === 11) {
        this.viewYear += 1;
        this.viewMonth = 0;
      } else {
        this.viewMonth += 1;
      }
    },
    selectCell(cell) {
      if (cell.isDisabled) return;
      this.updateDate(cell.dateStr);
      this.closePopup();
    },
    selectToday() {
      this.goToToday();
      this.closePopup();
    },
    handleClickOutside(event) {
      if (!this.showPopup) return;

      const wrapper = this.$refs.wrapper;
      const popup = this.$refs.popup;
      if (
        (wrapper && wrapper.contains(event.target)) ||
        (popup && popup.contains(event.target))
      ) {
        return;
      }
      this.closePopup();
    },
    updateDate(newDate) {
      if (this.validateDate(newDate)) {
        this.$emit('update:dateValue', newDate);
        this.updateUrl(newDate);
      }
    },
    validateDate(date) {
      if (!date) return false;
      
      const inputDate = new Date(date + 'T00:00:00');
      const minDate = new Date(this.minDate + 'T00:00:00');
      const maxDate = new Date(this.maxDate + 'T00:00:00');
      
      return inputDate >= minDate && inputDate <= maxDate && !isNaN(inputDate.getTime());
    },
    updateUrl(date) {
      const url = new URL(window.location);
      url.searchParams.set('date', date);
      window.history.pushState({}, '', url);
    },
    incrementDate() {
      if (!this.isMaxDate) {
        const newDate = new Date(this.dateValue + 'T00:00:00');
        newDate.setDate(newDate.getDate() + 1);
        this.updateDate(this.toYmd(newDate));
      }
    },
    decrementDate() {
      if (!this.isMinDate) {
        const newDate = new Date(this.dateValue + 'T00:00:00');
        newDate.setDate(newDate.getDate() - 1);
        this.updateDate(this.toYmd(newDate));
      }
    },
    goToToday() {
      this.updateDate(this.toYmd(new Date()));
    },
    goToYesterday() {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      this.updateDate(this.toYmd(yesterday));
    }
  },
};
</script>

<style scoped>
.date-picker-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.date-picker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-bg);
  box-shadow: 0 2px 8px var(--shadow-color);
  transition: all 0.3s ease;
  width: 100%;
  max-width: 700px;
  min-width: 500px;
  position: relative;
}

.date-picker:hover {
  border-color: var(--custom-color1);
  box-shadow: 0 4px 16px rgba(32, 204, 32, 0.15);
}

.date-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-bg);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px var(--shadow-color);
  flex-shrink: 0;
}

.date-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(32, 204, 32, 0.25);
  border-color: var(--custom-color1);
  color: var(--custom-color1);
  background: rgba(32, 204, 32, 0.08);
}

.date-button:disabled {
  background: var(--bg-tertiary);
  color: var(--text-tertiary);
  border-color: var(--border-color);
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
  opacity: 0.5;
}

.prev-button {
  margin-right: 12px;
}

.next-button {
  margin-left: 12px;
}

.button-icon {
  width: 20px;
  height: 20px;
}

.date-picker-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
}

.date-display {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.date-input-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: var(--bg-secondary);
  padding: 14px 20px;
  border-radius: 10px;
  min-width: 220px;
}

.date-field {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.date-field.is-open .date-input {
  border-color: var(--custom-color1);
  box-shadow: 0 0 0 3px rgba(32, 204, 32, 0.1);
}

.date-input {
  flex: 1;
  padding: 12px 40px 12px 16px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  background: var(--card-bg);
  color: var(--text-primary);
  transition: all 0.3s ease;
  min-width: 0;
  width: 100%;
  cursor: text;
}

.date-input:focus {
  outline: none;
  border-color: var(--custom-color1);
  box-shadow: 0 0 0 3px rgba(32, 204, 32, 0.1);
}

.date-input:hover {
  border-color: var(--border-color);
}

.calendar-icon-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-icon-btn svg {
  width: 18px;
  height: 18px;
}

.calendar-icon-btn:hover {
  color: var(--custom-color1);
  background: rgba(32, 204, 32, 0.08);
}

.calendar-popup {
  width: 280px;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-bg);
  box-shadow: 0 8px 24px var(--shadow-color);
}

.calendar-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cal-month-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}

.cal-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cal-nav-btn:hover:not(:disabled) {
  border-color: var(--custom-color1);
  color: var(--custom-color1);
  background: rgba(32, 204, 32, 0.08);
}

.cal-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 4px;
}

.calendar-weekdays span {
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
  color: var(--text-tertiary);
  text-transform: uppercase;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.cal-day {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cal-day:hover:not(:disabled) {
  background: rgba(32, 204, 32, 0.08);
  color: var(--custom-color1);
}

.cal-day.is-other-month {
  color: var(--text-tertiary);
  opacity: 0.5;
}

.cal-day.is-today:not(.is-selected) {
  border: 1px solid var(--custom-color1);
}

.cal-day.is-selected {
  background: var(--custom-color1);
  color: white;
}

.cal-day.is-selected:hover:not(:disabled) {
  background: var(--custom-color1);
  color: white;
}

.cal-day:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.calendar-popup-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--border-color);
}

.cal-footer-btn {
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--custom-color1);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cal-footer-btn:hover {
  background: rgba(32, 204, 32, 0.08);
}

.date-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.date-formatted {
  font-size: 0.95rem;
  color: var(--custom-color1);
  font-weight: 500;
  white-space: nowrap;
}

.date-separator {
  font-size: 0.95rem;
  color: var(--text-tertiary);
  font-weight: 400;
}

.day-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--custom-color1);
  white-space: nowrap;
}

.quick-button {
  padding: 10px 18px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px var(--shadow-color);
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
}

.quick-button:hover:not(:disabled) {
  border-color: var(--custom-color1);
  color: var(--custom-color1);
  background: rgba(32, 204, 32, 0.08);
  box-shadow: 0 3px 8px rgba(32, 204, 32, 0.25);
  transform: translateY(-1px);
}

.quick-button.active {
  background: linear-gradient(135deg, var(--custom-color1, #20cc20) 0%, #1bb31b 100%);
  color: white;
  border-color: var(--custom-color1, #20cc20);
  box-shadow: 0 3px 10px rgba(32, 204, 32, 0.35);
  cursor: default;
}

.quick-button.active:hover {
  background: linear-gradient(135deg, var(--custom-color1, #20cc20) 0%, #1bb31b 100%);
  color: white;
  transform: none;
  box-shadow: 0 3px 10px rgba(32, 204, 32, 0.35);
}

.quick-button:disabled {
  cursor: not-allowed;
}

.quick-button.active:disabled {
  cursor: default;
}

/* Responsive design */
@media (max-width: 750px) {
  .date-picker {
    width: 100%;
    max-width: 100%;
    min-width: auto;
    padding: 12px 16px;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .date-picker-center {
    flex-wrap: wrap;
    width: 100%;
    order: 1;
    margin-top: 8px;
    gap: 8px;
  }

  .date-picker-center .quick-button {
    flex: 1;
    min-width: calc(50% - 4px);
  }

  .date-display {
    width: 100%;
    order: 0;
  }
  
  .date-input-wrapper {
    width: 100%;
    min-width: auto;
    padding: 12px 16px;
  }
  
  .date-input {
    width: 100%;
    min-width: auto;
  }

  .date-button {
    width: 36px;
    height: 36px;
  }

  .prev-button {
    order: 0;
  }

  .next-button {
    order: 2;
  }
  
  .button-icon {
    width: 18px;
    height: 18px;
  }
  
  .day-name {
    font-size: 0.8rem;
  }
  
  .date-formatted {
    font-size: 1rem;
  }
  
  .quick-button {
    font-size: 0.85rem;
    padding: 8px 14px;
  }
}
</style>
